import { useCallback, useState } from 'react'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

import type { UseBenchmarkingSetupResult } from 'features/trend-detection/features/agent-create/hooks/use-benchmarking-setup'
import {
  runAndInvalidate,
  updateAgentConfig,
} from 'features/trend-detection/features/agent-create/hooks/use-benchmarking-update-submit-calls'
import { cohortFocusOverlapError } from 'features/trend-detection/features/benchmarking-agent/cohort-focus-overlap'
import {
  ResearchAgentsAction,
  trackResearchAgents,
} from 'features/trend-detection/mixpanel'
import type { TrendDetectionService } from 'features/trend-detection/service'

export interface UseBenchmarkingUpdateSubmitParams {
  projectId: string | null
  agentId: string | null
  service: TrendDetectionService
  benchmarkingSetup: UseBenchmarkingSetupResult
}

export interface UseBenchmarkingUpdateSubmitResult {
  saveAndRun: () => Promise<boolean>
  isSubmitting: boolean
}

/** Benchmarking Agent's edit-mode "Save and run": the same cohort/focus
 * overlap pre-check and `validate-filters` round-trip the create submit
 * runs, then `updateAgentConfig` (PATCH) in place of `saveAgent` (POST) —
 * the PATCH endpoint the deleted `useEditBenchmarkingAgent` hook used to
 * own — followed by `runAndInvalidate`. Returns whether the save succeeded
 * so the Container knows to navigate away. */
export function useBenchmarkingUpdateSubmit({
  projectId,
  agentId,
  service,
  benchmarkingSetup,
}: UseBenchmarkingUpdateSubmitParams): UseBenchmarkingUpdateSubmitResult {
  const queryClient = useQueryClient()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const saveAndRun = useCallback(async (): Promise<boolean> => {
    if (!projectId || !agentId || isSubmitting) return false

    const overlap = cohortFocusOverlapError(
      benchmarkingSetup.cohortFilters,
      benchmarkingSetup.focusGroupFilters,
      benchmarkingSetup.entityType,
    )
    if (overlap) {
      toast.error(overlap)
      return false
    }
    const { valid, errors } = await benchmarkingSetup.validateFilters()
    if (!valid) {
      errors.forEach((e) => toast.error(e))
      return false
    }

    setIsSubmitting(true)
    try {
      const updated = await updateAgentConfig(
        service,
        projectId,
        agentId,
        benchmarkingSetup,
      )
      if (!updated) return false

      trackResearchAgents(ResearchAgentsAction.agent_edit_save, {
        agent_type: 'benchmarking',
        agent_uuid: agentId,
        agent_name: benchmarkingSetup.agentName,
      })
      return await runAndInvalidate(
        service,
        queryClient,
        projectId,
        agentId,
        benchmarkingSetup.agentName,
      )
    } finally {
      setIsSubmitting(false)
    }
  }, [
    projectId,
    agentId,
    isSubmitting,
    benchmarkingSetup,
    service,
    queryClient,
  ])

  return { saveAndRun, isSubmitting }
}
